import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { BellRing, AlertTriangle, ClipboardCheck } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";

const SEEN_KEY = "gstdesk-reminders-seen";

export function useReminders() {
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/reminders").then((r) => setData(r.data)).catch(() => {}); }, []);
  return data;
}

const statusText = (i) => (i.overdue ? `Overdue by ${-i.days_left} day(s)` : i.days_left === 0 ? "Due today" : `${i.days_left} day(s) left`);
const itemUrl = (i) => {
  if (!i.annual) return `/clients/${i.client_id}?tab=returns&period=${i.period}`;
  const view = i.return_type.toLowerCase();
  const half = i.return_type === "ITC04" ? `&half=${i.period.split("-").pop()}` : "";
  return `/clients/${i.client_id}?tab=annual&fy=${i.fy}&view=${view}${half}`;
};

export function ReminderList({ items, onOpen }) {
  return (
    <div className="divide-y divide-border">
      {items.map((i) => (
        <button key={`${i.client_id}-${i.return_type}-${i.period}`} data-testid={`reminder-item-${i.client_id}-${i.return_type}-${i.period}`}
          onClick={() => onOpen(i)} className="w-full flex items-center justify-between gap-3 py-2.5 text-left hover:bg-secondary/50 px-2 rounded transition-colors">
          <div className="min-w-0">
            <div className="text-sm font-medium truncate">{i.client_name}</div>
            <div className="text-xs text-muted-foreground font-mono">{i.label} · {i.period} · due {i.due_date}</div>
          </div>
          <span className={`shrink-0 text-xs font-medium ${i.overdue ? "text-rose-400" : "text-amber-400"}`}>{statusText(i)}</span>
        </button>
      ))}
    </div>
  );
}

export function RemindersBanner() {
  const data = useReminders();
  const nav = useNavigate();
  if (!data?.items?.length) return null;
  return (
    <div className="rounded-lg border border-amber-500/30 bg-amber-500/5 p-5" data-testid="reminders-banner">
      <div className="flex items-center gap-2 mb-2">
        <BellRing className="h-4 w-4 text-amber-400" />
        <span className="font-head font-semibold">Filing reminders</span>
        <span className="text-xs text-muted-foreground">GSTR-1 / 3B due within {data.window_days} days, GSTR-9 / 9C / ITC-04 within {data.annual_window_days} days, and overdue returns</span>
      </div>
      <ReminderList items={data.items} onOpen={(i) => nav(itemUrl(i))} />
    </div>
  );
}

export function LoginRemindersDialog() {
  const data = useReminders();
  const [reg, setReg] = useState(null);
  const { user } = useAuth();
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  useEffect(() => { api.get("/registration-check").then((r) => setReg(r.data)).catch(() => {}); }, []);
  const has = (data?.items?.length || 0) + (reg?.items?.length || 0) > 0;
  useEffect(() => {
    if (data && reg && has && !sessionStorage.getItem(SEEN_KEY)) { setOpen(true); sessionStorage.setItem(SEEN_KEY, "1"); }
  }, [data, reg, has]);
  if (!has) return null;
  const items = data?.items || [];
  const overdue = items.filter((i) => i.overdue).length;
  const go = (url) => { setOpen(false); nav(url); };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="bg-card max-w-lg w-[calc(100%-2rem)] rounded-lg max-h-[90vh] overflow-y-auto" data-testid="reminders-dialog">
        {items.length > 0 && <>
          <DialogHeader>
            <DialogTitle className="font-head flex items-center gap-2"><BellRing className="h-5 w-5 text-amber-400" /> Upcoming GST filings</DialogTitle>
            <DialogDescription>
              {items.filter((i) => !i.overdue).length} upcoming (monthly within {data.window_days} days, annual within {data.annual_window_days} days){overdue ? `, ${overdue} overdue` : ""}.
            </DialogDescription>
          </DialogHeader>
          {overdue > 0 && <div className="flex items-center gap-2 text-xs text-rose-400"><AlertTriangle className="h-3.5 w-3.5" /> Overdue returns may attract late fees and interest.</div>}
          <div className="max-h-64 overflow-y-auto">
            <ReminderList items={items} onOpen={(i) => go(itemUrl(i))} />
          </div>
        </>}
        {reg?.items?.length > 0 && <RegistrationCheck reg={reg} setReg={setReg} first={items.length === 0} canVerify={user?.role !== "reader"} onOpen={(c) => go(`/clients/${c.id}`)} />}
        <button data-testid="reminders-dialog-close" onClick={() => setOpen(false)} className="mt-2 w-full rounded-md bg-indigo-600 hover:bg-indigo-700 text-white py-2 text-sm font-medium">OK, got it</button>
      </DialogContent>
    </Dialog>
  );
}

function RegistrationCheck({ reg, setReg, canVerify, onOpen, first }) {
  const verify = async (c) => {
    try {
      await api.post(`/clients/${c.id}/registration-verified`);
      setReg({ ...reg, items: reg.items.filter((x) => x.id !== c.id) });
      toast.success(`${c.legal_name}: registration details confirmed`);
    } catch (e) { toast.error(apiError(e)); }
  };
  return (
    <div className={`space-y-2 ${first ? "" : "border-t border-border pt-4"}`} data-testid="registration-check">
      {first ? (
        <DialogHeader><DialogTitle className="font-head flex items-center gap-2"><ClipboardCheck className="h-5 w-5 text-indigo-400" /> Check your registration details are duly updated</DialogTitle>
          <DialogDescription>Confirm on the GST portal that address, business activity, authorised signatory and contact details are current. Asked every {reg.interval_days} days per client.</DialogDescription></DialogHeader>
      ) : <>
        <div className="flex items-center gap-2 font-head font-semibold"><ClipboardCheck className="h-5 w-5 text-indigo-400" /> Check your registration details are duly updated</div>
        <p className="text-xs text-muted-foreground">Confirm on the GST portal that address, business activity, authorised signatory and contact details are current. Asked every {reg.interval_days} days per client.</p>
      </>}
      <div className="divide-y divide-border">
        {reg.items.map((c) => (
          <div key={c.id} className="flex items-center justify-between gap-3 py-2" data-testid={`registration-item-${c.id}`}>
            <button onClick={() => onOpen(c)} className="min-w-0 text-left">
              <div className="text-sm font-medium truncate">{c.legal_name}</div>
              <div className="text-xs text-muted-foreground font-mono">{c.gstin} · {c.registration_verified_at ? `last confirmed ${new Date(c.registration_verified_at).toLocaleDateString("en-IN")}` : "never confirmed"}</div>
            </button>
            {canVerify && <button data-testid={`registration-verify-${c.id}`} onClick={() => verify(c)}
              className="shrink-0 rounded-md border border-indigo-500/40 text-indigo-400 hover:bg-indigo-500/10 px-2.5 py-1 text-xs font-medium transition-colors">Details are up to date</button>}
          </div>
        ))}
      </div>
    </div>
  );
}
